import { StorageBuckets } from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

import { showIfUploadedShown } from './dancer-media-visibility';
import { MAX_HEAD_READS, sleep } from './dancer-media.constants';
import type { DancerMediaContext } from './dancer-media.context';
import { normalizeContentHash } from './dancer-media.mappers';

/**
 * Idempotent-upload guard (BAL-700): look up an existing, live
 * (requires_reupload = false) artist_media row that a new upload would
 * duplicate. Uploaders (uploadMedia, finalizeDirectUpload) call this
 * before inserting so a re-upload of an already-uploaded file returns the
 * existing row instead of creating a duplicate — the only previous
 * uniqueness guard was idx_artist_media_unique_headshot (one headshot per
 * user), which never covered photos, videos, or documents.
 *
 * A row is a duplicate if it matches the same (user_id, media_type) AND
 * either the same file_name OR the same content_hash (identical bytes
 * saved under a different name). A file_name match whose bytes are known
 * to differ (both hashes set and unequal) is a different file, not a
 * duplicate. The content_hash branch also makes the
 * common same-bytes-rename case return gracefully rather than surfacing
 * as a raw unique-violation from idx_artist_media_unique_content_hash.
 *
 * Excludes requires_reupload rows (Meteor lost-file placeholders) — those
 * genuinely need fresh bytes, not a return of the same dead reference.
 * Bounded to at most two lookups (filename, then hash) — never per-row, so
 * no N+1.
 */
export async function findExistingUploadedMedia(
  ctx: DancerMediaContext,
  userId: string,
  mediaType: string,
  fileName: string,
  contentHash?: string | null,
  {
    matchName = true,
    excludeStoragePath,
  }: { matchName?: boolean; excludeStoragePath?: string } = {},
): Promise<Database['public']['Tables']['artist_media']['Row'] | null> {
  // Every live row of this user and type; the caller adds one match column.
  // A row at `excludeStoragePath` never matches. The filter is added only
  // when a path is given: `neq` on a NULL storage_path is not true in SQL,
  // so an unconditional one would also drop link-only rows.
  const candidates = () => {
    // eslint-disable-next-line supabase-query/no-unbounded-select -- both callers of candidates() chain .limit(1).maybeSingle() before awaiting
    const query = ctx.client
      .from('artist_media')
      .select('*')
      .eq('user_id', userId)
      .eq('media_type', mediaType)
      .eq('requires_reupload', false)
      // A copy an admin retired as redundant must not block a later re-upload.
      .is('lost_at', null);
    return excludeStoragePath
      ? query.neq('storage_path', excludeStoragePath)
      : query;
  };

  if (matchName) {
    const { data: byName } = await candidates()
      .eq('file_name', fileName)
      .limit(1)
      .maybeSingle();
    // A name is not an identity: `image.jpg` is the iOS share-sheet default
    // for every photo. When both hashes are known and differ, this is a
    // different file, and resolving it to the old row would discard the new
    // upload (and, for a private row, could make that row public).
    const differentBytes =
      !!byName?.content_hash &&
      !!contentHash &&
      byName.content_hash !== contentHash;
    if (byName && !differentBytes) return byName;
  }

  if (!contentHash) return null;

  const { data: byHash } = await candidates()
    .eq('content_hash', contentHash)
    .limit(1)
    .maybeSingle();
  return byHash ?? null;
}

/**
 * Fetch a storage object's existence + byte-content hash in one round-trip
 * (BAL-700 / BAL-595). Uses storage.info(), whose response carries the S3
 * ETag; normalizeContentHash turns a single-PUT ETag into the md5 content
 * hash (and returns null for multipart / non-md5 ETags).
 *
 * Retries with bounded backoff (mirrors MediaWriteService.objectExistsWithRetry,
 * BALLEE-G7): the browser PUT and this server-side `.info()` read reach Supabase
 * Storage over two independent connections, so under concurrent load the object
 * metadata can lag behind a PUT the client already saw succeed (read-after-write).
 * A single miss must not strand the just-uploaded file. A still-missing object
 * after all attempts reports exists=false — the caller fails closed.
 */
export async function fetchStorageObjectHash(
  ctx: DancerMediaContext,
  storagePath: string,
  attempts = 4,
  delayMs = 300,
): Promise<{ exists: boolean; contentHash: string | null }> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
    }
    const { data, error } = await ctx.client.storage
      .from(StorageBuckets.DANCER_MEDIA)
      .info(storagePath);
    if (!error && data) {
      const rawEtag = data.metadata?.eTag ?? data.etag ?? null;
      return { exists: true, contentHash: normalizeContentHash(rawEtag) };
    }
  }
  return { exists: false, contentHash: null };
}

/**
 * Read the first `byteCount` bytes of a stored object (BAL-747).
 *
 * Streams the download and cancels the reader as soon as enough bytes have
 * arrived, so a 10 MB CV costs one chunk off the wire — not a full fetch into
 * the function. Supabase Storage has no range parameter on `download()`, so
 * early cancellation is how the read stays bounded.
 *
 * Retries with the same bounded backoff as {@link fetchStorageObjectHash} and
 * for the same reason: the browser PUT and this server-side read reach Supabase
 * over two independent connections, so under load the object can lag behind a
 * PUT the client already saw succeed (BALLEE-G7 — 87 orphan objects during the
 * GDO apply burst 2026-07-09). Without retries this read would turn that lag
 * into a hard finalize failure for EVERY document, including the ones that
 * already worked before the sniff existed.
 *
 * Retries cover a failed download only. A download that succeeds and yields
 * nothing returns an empty array, not null — an object that streams cleanly to
 * zero bytes is a verdict ("not a PDF"), and retrying it would loop forever.
 * Null means genuinely unreadable, which callers treat as unknown.
 */
export async function readStorageHead(
  ctx: DancerMediaContext,
  storagePath: string,
  byteCount: number,
  attempts = 4,
  delayMs = 300,
): Promise<Uint8Array | null> {
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (attempt > 0) {
      await sleep(delayMs * attempt);
    }

    try {
      const { data, error } = await ctx.client.storage
        .from(StorageBuckets.DANCER_MEDIA)
        .download(storagePath)
        .asStream();

      if (error || !data) continue;

      const reader = data.getReader();
      const chunks: Uint8Array[] = [];
      let total = 0;
      // Zero-length chunks are legal and make no progress, so `total` alone
      // cannot terminate the loop. Bound the reads too.
      let readsLeft = MAX_HEAD_READS;
      // Did the stream end on its own, or did we hit the read cap? Only the
      // former yields a head we can judge — see below.
      let streamEnded = false;

      try {
        while (total < byteCount && readsLeft-- > 0) {
          const { done, value } = await reader.read();
          if (done) {
            streamEnded = true;
            break;
          }
          if (!value?.length) continue;
          chunks.push(value);
          total += value.length;
        }
      } finally {
        // Hang up on the rest of the file. Safe after `done` too — cancelling
        // a closed stream is a no-op.
        await reader.cancel().catch(() => undefined);
      }

      // Hitting the read cap without either filling the sniff window or
      // reaching the end of the stream leaves a SHORT head. That is not a
      // verdict — the caller deletes the object on a head that fails the
      // signature check, and `%PDF-` may simply lie past what we read. Report
      // it as unknown so a legitimate CV is never destroyed by a pathological
      // chunk pattern.
      if (total < byteCount && !streamEnded) return null;

      const head = new Uint8Array(Math.min(total, byteCount));
      let written = 0;
      for (const chunk of chunks) {
        if (written >= head.length) break;
        const take = Math.min(chunk.length, head.length - written);
        head.set(chunk.subarray(0, take), written);
        written += take;
      }
      return head;
    } catch {
      // Transient — fall through to the next attempt.
    }
  }

  return null;
}

/**
 * A re-upload can land on an existing row (the BAL-700 duplicate guard, or
 * the RPC's ON CONFLICT). An upload from a surface that shows its items (the
 * profile editor) then shows that row if it is private (R4). It never hides
 * one: attaching a shown photo to an application keeps it on the profile.
 * A failure is logged and the row returned as it is: the upload itself
 * succeeded, and the dancer can still switch the item on in the library.
 */
export async function showWhenUploadedShown(
  ctx: DancerMediaContext,
  row: Database['public']['Tables']['artist_media']['Row'],
  shownOnProfile: boolean | undefined,
  logContext: Record<string, unknown>,
): Promise<Database['public']['Tables']['artist_media']['Row']> {
  const shown = await showIfUploadedShown(
    ctx.client,
    () => ctx.getR2Service(),
    row,
    shownOnProfile,
  );
  if (shown.isSuccess) return shown.getValue();
  ctx.log('warn', 'Could not show a re-uploaded private item', {
    ...logContext,
    mediaId: row.id,
    error: shown.getError().message,
  });
  return row;
}
